import { type FieldValues } from 'react-hook-form';
import isPlainObject from 'lodash.isplainobject';

import { type TOptionWithTwoDescriptions } from '../../../components/OptionWithTwoDescriptions/OptionWithTwoDescriptions';
import { type IAttachmentFormValues } from '../components/Attachments/types';
import { type ISavedItem } from '../components/Items/types';

import { type IAdditionalFieldConfig } from '../types/response';
import { FieldPrefix } from '../constants/fieldPrefixes';

import { formatDateForBackend } from '../../../helpers/dateHelpers';
import { replaceSpecialCharacters } from './importExportFieldName';

function getSelectValue<T extends SelectOption>(value: T | null | undefined) {
  return value?.value ?? null;
}

function getSelectTagLabel<T extends SelectOption>(value: T | null | undefined) {
  return value?.tag?.label ?? null;
}

function getSelectDescription<T extends SelectOption>(value: T | null | undefined) {
  return (value?.label as { title: string; description: string })?.description ?? null;
}

function getAdditionalFieldValue(value: unknown) {
  if (value instanceof Date) {
    return formatDateForBackend(value);
  }

  if (isPlainObject(value)) {
    return (value as SelectOption)?.value;
  }

  if (Array.isArray(value)) {
    return value.map((v) => v.value);
  }

  return value;
}

function getAdditionalFieldsExportData(
  additionalFieldValues: Record<string, string | Date | number | SelectOption | null>,
  licenseTypeAdditionalFields: IAdditionalFieldConfig[]
) {
  return Object.entries(additionalFieldValues).map(([key, value]) => ({
    fieldName: replaceSpecialCharacters(
      licenseTypeAdditionalFields.find((f) => f.id === Number(key.slice(FieldPrefix.additionalField.length)))
        ?.englishLabel || ''
    ),
    fieldValue: getAdditionalFieldValue(value),
  }));
}

function getDbOptionAdditionalFieldsExportData(
  dbOptionAdditionalFieldValues: Record<string, TOptionWithTwoDescriptions>,
  licenseTypeAdditionalFieldss: IAdditionalFieldConfig[]
) {
  return Object.entries(dbOptionAdditionalFieldValues).map(([key, value]) => ({
    fieldName: replaceSpecialCharacters(
      licenseTypeAdditionalFieldss.find((f) => f.id === Number(key.slice(FieldPrefix.additionalField.length)))
        ?.englishLabel || ''
    ),
    fieldCode: value?.label ?? null,
    fieldValue1: value?.name ?? null,
    fieldValue2: value?.name2 ?? null,
    optionId: value?.value ?? null,
  }));
}

function getItemExportData(items: ISavedItem[], licenseTypeAdditionalFields: IAdditionalFieldConfig[]) {
  return items.map((item: ISavedItem) => {
    const {
      id,
      additionalFields,
      dbOptionAdditionalFields,
      requestedUomCode,
      requestedUomCode2,
      approvedUomCode,
      approvedUomCode2,
      commodityCode,
      countryOfOriginCode,
      packageTypeCode,
      treatments,
      packages,
      ...rest
    } = item;

    return {
      ...rest,
      requestedUomCode: getSelectValue(requestedUomCode),
      requestedUomCode2: getSelectValue(requestedUomCode2),
      requestedUomName: getSelectDescription(requestedUomCode),
      requestedUomName2: getSelectDescription(requestedUomCode2),
      approvedUomCode: getSelectValue(approvedUomCode),
      approvedUomCode2: getSelectValue(approvedUomCode2),
      approvedUomName: getSelectDescription(approvedUomCode),
      approvedUomName2: getSelectDescription(approvedUomCode2),
      commodityCode: getSelectValue(commodityCode),
      countryOfOriginCode: getSelectValue(countryOfOriginCode),
      countryOfOriginName: getSelectTagLabel(countryOfOriginCode),
      packageTypeCode: getSelectValue(packageTypeCode),
      packageTypeName: getSelectTagLabel(packageTypeCode),
      additionalFields: getAdditionalFieldsExportData(additionalFields, licenseTypeAdditionalFields),
      dbOptionAdditionalFields: getDbOptionAdditionalFieldsExportData(
        dbOptionAdditionalFields,
        licenseTypeAdditionalFields
      ),
      packages: packages?.map(({ id, ...rest }) => rest) ?? [],
      treatments: treatments?.map(({ id, ...rest }) => rest) ?? [],
    };
  });
}

function getAttachmentExportData(attachedDocuments: IAttachmentFormValues[]) {
  return attachedDocuments.map((attachedDocument) => {
    const { id, code, fileUrl, parentAttachmentId, ...rest } = attachedDocument;

    return {
      ...rest,
      code: getSelectValue(code),
      name: getSelectTagLabel(code),
      fileUrl: null,
    };
  });
}

function convertFormDataForExport(values: FieldValues, licenseTypeAdditionalFields: IAdditionalFieldConfig[]) {
  const {
    id,
    items,
    attachedDocuments,
    beneficiaries,
    fees,
    additionalFields,
    dbOptionAdditionalFields,
    transactionRecords,
    companyCode,
    countryOfDestinationCode,
    customsOfficeCode,
    departmentOfficeCode,
    invoiceCurrencyCode,
    licenseType,
    placeOfLoadingCode,
    placeOfUnloadingCode,
    termsOfDeliveryCode,
    typeOfUse,
    transitCountryCode,
    meanOfTransportCode,
    ...rest
  } = values;

  return {
    ...rest,
    licenseType: getSelectValue(licenseType),
    companyCode: getSelectValue(companyCode),
    companyDescription: getSelectTagLabel(companyCode),
    countryOfDestinationCode: getSelectValue(countryOfDestinationCode),
    countryOfDestinationName: getSelectTagLabel(countryOfDestinationCode),
    customsOfficeCode: getSelectValue(customsOfficeCode),
    customsOfficeName: getSelectTagLabel(customsOfficeCode),
    departmentOfficeCode: getSelectValue(departmentOfficeCode),
    departmentOfficeName: getSelectTagLabel(departmentOfficeCode),
    invoiceCurrencyCode: getSelectValue(invoiceCurrencyCode),
    invoiceExchangeRate: getSelectTagLabel(invoiceCurrencyCode),
    placeOfLoadingCode: getSelectValue(placeOfLoadingCode),
    placeOfLoadingName: getSelectTagLabel(placeOfLoadingCode),
    placeOfUnloadingCode: getSelectValue(placeOfUnloadingCode),
    placeOfUnloadingName: getSelectTagLabel(placeOfUnloadingCode),
    termsOfDeliveryCode: getSelectValue(termsOfDeliveryCode),
    termsOfDeliveryDescription: getSelectTagLabel(termsOfDeliveryCode),
    typeOfUse: getSelectValue(typeOfUse),
    transitCountryCode: getSelectValue(transitCountryCode),
    transitCountryName: getSelectTagLabel(transitCountryCode),
    meanOfTransportCode: getSelectValue(meanOfTransportCode),
    meanOfTransportName: getSelectTagLabel(meanOfTransportCode),

    items: getItemExportData(items as ISavedItem[], licenseTypeAdditionalFields),
    attachedDocuments: getAttachmentExportData(attachedDocuments as IAttachmentFormValues[]),
    additionalFields: getAdditionalFieldsExportData(
      additionalFields as Record<string, string | Date | number | SelectOption>,
      licenseTypeAdditionalFields
    ),
    dbOptionAdditionalFields: getDbOptionAdditionalFieldsExportData(
      dbOptionAdditionalFields as Record<string, TOptionWithTwoDescriptions>,
      licenseTypeAdditionalFields
    ),
  };
}

export default convertFormDataForExport;
